// React Query
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

// Utils
import queryString from 'api/utils/queryStringBuilder';
import { getRequest as get } from '../utils/HttpMethods';
import {
  createSubmission as create,
  updateEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
} from '../utils/queries';

// Interface
import {
  CreateSubmission,
  Submission,
  isSubmission,
  isSubmissionWithCount,
  SubmissionWithCount,
  SubmissionType,
} from 'interfaces/submission';
import {
  hasCompleteDateRange,
  hasEmptyDateRange,
} from 'utils/submissionCriteria';

export type SubmissionCountResponse =
  | number
  | {
      claimsCount?: number;
      claimCount?: number;
      errors?: string[];
    };

const canValidateSubmission = ({
  type,
  startDate,
  endDate,
  providerID,
  payorType,
}: {
  type: SubmissionType;
  startDate: string | null;
  endDate: string | null;
  providerID: string | null;
  payorType: string | null;
}) => {
  if (!type || !payorType) return false;

  switch (type) {
    case SubmissionType.ready:
      return true;
    case SubmissionType.date:
      return hasCompleteDateRange(startDate, endDate);
    case SubmissionType.provider:
      return (
        !!providerID &&
        (hasEmptyDateRange(startDate, endDate) ||
          hasCompleteDateRange(startDate, endDate))
      );
  }
};

export const submissionQueryKeys = {
  all: ['submission'],
  list: () => [...submissionQueryKeys.all, 'list'],
  listBySite: (siteID: string | string[]) => [
    ...submissionQueryKeys.all,
    siteID,
  ],
  get: (id: string) => [...submissionQueryKeys.all, id],
  revert: () => [...submissionQueryKeys.all, 'revert'],
  count: ({
    siteID,
    type,
    startDate,
    endDate,
    providerID,
    payorType,
  }: {
    siteID: string;
    type: SubmissionType;
    startDate: string | null;
    endDate: string | null;
    providerID: string;
    payorType: string;
  }) => [
    'submissionCount',
    siteID,
    type,
    startDate,
    endDate,
    providerID,
    payorType,
  ],
};

// Hooks

/**
 * @description Lists all submissions.
 * @returns {Promise<Submission[]>} An array of submissions.
 */
const listSubmissions = async (): Promise<Submission[]> =>
  await get<Submission[]>('QBilling', `/resource/submission/all`);

/**
 * @description Hook to list all submissions.
 * @returns {UseQueryResult<Submission[], Error>} The query result containing an array of submissions.
 */
export const useListSubmissions = (): UseQueryResult<Submission[], Error> => {
  return useListQuery<Submission>({
    queryKey: submissionQueryKeys.all,
    fetchFunction: listSubmissions,
    functionName: 'listSubmissions',
    isEntity: isSubmission,
    enabled: true,
  });
};

/**
 * @description Fetches a submission by ID.
 * @param {string} id - The submission ID.
 * @returns {Promise<Submission | null>} The submission data or null if not found.
 */
const getSubmission = async (id: string): Promise<Submission | null> =>
  await get<Submission>(
    'QBilling',
    `/resource/submission/${queryString({ id })}`
  );

/**
 * @description Hook to fetch a submission by ID.
 * @param {Object} params - The parameters.
 * @param {string} params.id - The submission ID.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Submission | null, Error>} The query result containing the submission data or null if not found.
 */
export const useGetSubmission = ({
  id,
  displayNotification = false,
}: {
  id: string;
  displayNotification?: boolean;
}): UseQueryResult<Submission | null, Error> => {
  return useGetQuery<Submission>({
    queryKey: submissionQueryKeys.get(id),
    fetchFunction: getSubmission,
    functionName: 'getSubmission',
    params: id,
    isEntity: isSubmission,
    enabled: !!id,
    displayNotification,
  });
};

/**
 * @description Lists submissions by site ID.
 * @param {string} siteID - The site ID.
 * @returns {Promise<SubmissionWithCount[]>} Submission summaries with claim counts.
 */
const listSubmissionsBySite = async ({
  siteID,
}: {
  siteID: string | string[];
}): Promise<SubmissionWithCount[]> =>
  await get<SubmissionWithCount[]>(
    'QBilling',
    `/resource/submission/site${queryString({ siteID })}`
  );

/**
 * @description Hook to list submissions by site ID.
 * @param {Object} params - The parameters.
 * @param {string} params.siteID - The site ID.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<SubmissionWithCount[], Error>} The submission summaries query result.
 */
export const useListSubmissionsBySite = ({
  siteID,
  displayNotification = false,
  refetchInterval = -1,
}: {
  siteID: string | string[];
  displayNotification?: boolean;
  refetchInterval?: number;
}): UseQueryResult<SubmissionWithCount[], Error> => {
  return useListQuery<SubmissionWithCount>({
    queryKey: submissionQueryKeys.listBySite(siteID),
    fetchFunction: listSubmissionsBySite,
    functionName: 'listSubmissionsBySite',
    isEntity: isSubmissionWithCount,
    params: { siteID },
    enabled: Array.isArray(siteID) ? siteID.length > 0 : !!siteID,
    displayNotification,
    refetchInterval,
  });
};

/**
 * @description Fetches submissions by claim ID.
 * @param {string} claimID - The claim ID.
 * @returns {Promise<Submission[] | null>} An array of submissions or null if not found.
 */
const getSubmissionByClaimID = async (
  claimID: string
): Promise<Submission[] | null> =>
  await get<Submission[]>(
    'QBilling',
    `/resource/claim/submission/id${queryString({ claimID })}`
  );

/**
 * @description Hook to fetch submissions by claim ID.
 * @param {Object} params - The parameters.
 * @param {string} params.claimID - The claim ID.
 * @param {boolean} [params.displayNotification=false] - Whether to display a notification.
 * @returns {UseQueryResult<Submission[], Error>} The query result containing an array of submissions or null if not found.
 */
export const useGetSubmissionByClaimID = ({
  claimID,
  displayNotification = false,
}: {
  claimID: string;
  displayNotification?: boolean;
}): UseQueryResult<Submission[], Error> => {
  return useListQuery<Submission>({
    queryKey: submissionQueryKeys.get(claimID),
    fetchFunction: getSubmissionByClaimID,
    functionName: 'getSubmissionByClaimID',
    params: claimID,
    isEntity: isSubmission,
    enabled: !!claimID,
    displayNotification,
  });
};

/**
 * @description Creates a submission by ready status.
 * @param {CreateSubmission} submission - The submission data.
 * @returns {Promise<Submission | null>} The created submission or null if failed.
 */
const createSubmission = async (
  submission: CreateSubmission,
  type: SubmissionType
): Promise<Submission | null> => {
  return create({
    functionName: 'createSubmission',
    isSubmission: isSubmission,
    params: submission,
    path: `/resource/submission/${type}`,
  });
};

/**
 * @description Updates an existing submission.
 * @param {Submission} submission - The submission data.
 * @returns {Promise<Submission | null>} The updated submission or null if failed.
 */
const updateSubmission = async (
  submission: Submission
): Promise<Submission | null> => {
  return updateEntity<Submission>({
    entity: submission,
    functionName: 'updateSubmission',
    isEntity: isSubmission,
    path: '/resource/submission',
  });
};

/**
 * @description Reverts an existing submission.
 * @param {Submission} submission - The submission data.
 * @returns {Promise<Submission | null>} The updated submission or null if failed.
 */
const revertSubmission = async (
  submission: Submission
): Promise<Submission | null> => {
  return updateEntity<Submission>({
    entity: submission,
    functionName: 'revertSubmission',
    isEntity: isSubmission,
    path: '/resource/submission/revert',
  });
};

/**
 * @description Hook to create a submission.
 * @returns {UseMutationResult<Submission | null, Error>} The mutation result for creating a submission.
 */
export const useCreateSubmission = (): UseMutationResult<
  Submission | null,
  Error
> => {
  return useMutationQuery({
    errorMessage: 'Submission Creation Failed',
    mutationFn: ({
      data,
      type,
    }: {
      data: CreateSubmission;
      type: SubmissionType;
      email?: string;
    }) => {
      return createSubmission(data, type);
    },
    successMessage: 'Submission Created',
    queryKey: submissionQueryKeys.all,
  });
};

/**
 * @description Hook to update an existing submission.
 * @returns {UseMutationResult<Submission | null, Error>} The mutation result for updating a submission.
 */
export const useUpdateSubmission = (): UseMutationResult<
  Submission | null,
  Error
> => {
  return useMutationQuery({
    errorMessage: 'Submission Update Failed',
    mutationFn: (params: { id: string; status: string; uploaded: boolean }) =>
      updateSubmission(params as Submission),
    successMessage: 'Submission Updated',
    queryKey: submissionQueryKeys.all,
  });
};

export const useRevertSubmission = (): UseMutationResult<
  Submission | null,
  Error
> => {
  return useMutationQuery({
    errorMessage: 'Submission Revert Failed',
    mutationFn: (params: { id: string }) =>
      revertSubmission(params as Submission),
    successMessage: 'Submission Reverted',
    queryKey: submissionQueryKeys.revert(),
  });
};

const validateSubmission = async ({
  siteID,
  type,
  startDate,
  endDate,
  providerID,
  payorType,
}: {
  siteID: string;
  type: SubmissionType;
  startDate: string | null;
  endDate: string | null;
  providerID: string | null;
  payorType: string | null;
}): Promise<SubmissionCountResponse> => {
  const params: any = {
    siteID,
    type,
    payorType,
  };
  switch (type) {
    case SubmissionType.date:
      params.startDate = startDate;
      params.endDate = endDate;
      break;
    case SubmissionType.provider:
      params.providerID = providerID;
      if (hasCompleteDateRange(startDate, endDate)) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      break;
  }
  return await get<SubmissionCountResponse | null>(
    'QBilling',
    `/resource/submission/validate${queryString(params)}`
  );
};

/**
 * @description Hook to get count of claims in submission.
 */
export const useValidateSubmission = ({
  siteID,
  type,
  startDate,
  endDate,
  providerID,
  payorType,
}: {
  siteID: string;
  type: SubmissionType;
  startDate: string | null;
  endDate: string | null;
  providerID: string;
  payorType: string;
}): UseQueryResult<SubmissionCountResponse, Error> => {
  const isValidationReady = canValidateSubmission({
    type,
    startDate,
    endDate,
    providerID,
    payorType,
  });

  return useGetQuery<SubmissionCountResponse>({
    queryKey: submissionQueryKeys.count({
      siteID,
      type,
      startDate,
      endDate,
      providerID,
      payorType,
    }),
    fetchFunction: ({
      siteID,
      type,
      startDate,
      endDate,
      providerID,
      payorType,
    }) =>
      validateSubmission({
        siteID,
        type,
        startDate,
        endDate,
        providerID,
        payorType,
      }),
    isEntity: (obj: any): obj is SubmissionCountResponse =>
      typeof obj === 'number' || (!!obj && typeof obj === 'object'),
    functionName: 'getSubmissionCount',
    params: { siteID, type, startDate, endDate, providerID, payorType },
    enabled: !!siteID && isValidationReady,
  });
};
